import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { AuthService } from 'src/app/auth/auth.service';
import { Organization } from 'src/app/internal-user/customer-management/models/organization';
import { DeploymentTypeMetadata, FeatureSelectedEventArgs, InputMode } from '../../models/deploymentForm.model';
import { SystemFormService } from '../../services/system-form.service';

/**
 * Component containing a form that is dynamically constructed from a given deployment's metadata.
 * 
 */
@Component({
    selector: 'app-system-form',
    templateUrl: './system-form.component.html',
    styleUrls: ['./system-form.component.scss'],
    standalone: false
})
export class SystemFormComponent implements OnInit, OnDestroy {
  /** The form to use; can be a nested group on a parent form. */
  @Input() form: FormGroup;
  @Input() mode: InputMode;
  /** The form's associated metadata. */
  @Input() systemFormMetadata: DeploymentTypeMetadata;
  @Input() organizationId: string;
  @Input() recursionDepth?: number = 0;
  @Input() selectedOrganization: Organization | null = null;
  /** The currently selected site within this system form. Populated by site-select output and threaded to recursive children. */
  @Input() selectedSite: Organization | null = null;

  @Output() featureSelected = new EventEmitter<FeatureSelectedEventArgs>();

  canEditAdvancedOptions: boolean;
  isExpanded: boolean = true;
  selectedFeatures: string[];

  get featureName(): string { return this.systemFormMetadata.friendlyName; }
  get optionalFeatures(): FormArray { return this.form.get('optionalFeatures') as FormArray; }

  /** Optional feature names filtered by LaunchDarkly feature flags. Features whose flag evaluates to false are hidden from the selector. */
  optionalFeatureNames: string[] = [];

  /** Sorted required parameters by sortOrder (ascending) if present. */
  get sortedRequiredParameters() {
    return [...(this.systemFormMetadata?.requiredParameters || [])]
      .sort((a: any, b: any) => {
        const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
        const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
        if (ao === bo) return (a.name || '').localeCompare(b.name || '');
        return ao - bo;
      });
  }

  /** Sorted optional parameters by sortOrder (ascending) if present. */
  get sortedOptionalParameters() {
    return [...(this.systemFormMetadata?.optionalParameters || [])]
      .sort((a: any, b: any) => {
        const ao = typeof a.sortOrder === 'number' ? a.sortOrder : Number.MAX_SAFE_INTEGER;
        const bo = typeof b.sortOrder === 'number' ? b.sortOrder : Number.MAX_SAFE_INTEGER;
        if (ao === bo) return (a.name || '').localeCompare(b.name || '');
        return ao - bo;
      });
  }

  /** Determines if any dynamic controls are currently being shown to the user */
  get featureControlsShown(): boolean {
    const hasRequiredOptions = this.systemFormMetadata.requiredParameters.length > 0;
    const hasAdvancedOptions = this.systemFormMetadata.optionalParameters.length > 0;
    
    if (this.canEditAdvancedOptions && (hasRequiredOptions || hasAdvancedOptions)) {
      return true;
    }

    if (!this.canEditAdvancedOptions && hasRequiredOptions) {
      return true;
    }

    return false;
  }
  get isParentForm(): boolean { return this.recursionDepth === 0; }

  constructor(
    private authService: AuthService,
    private systemFormService: SystemFormService
  ) { }

  ngOnInit(): void {
    // Compute the cached list of optional feature names that pass LaunchDarkly flag checks.
    this.optionalFeatureNames = this.systemFormMetadata?.optionalFeatures
      ?.filter(feature => this.systemFormService.isFeatureEnabled(feature) && !this.isDeploymentScopedFeature(feature.name))
      .map(feature => feature.friendlyName) || [];

    this.authService.profile$.subscribe({
      next: (profile) => {
        this.canEditAdvancedOptions = profile.hasRole("Parata Dev");
      },
      error: (err) => {
        console.error('Could not determine if current user has Parata Dev role, removing advanced options.');
        console.error(err);
        this.canEditAdvancedOptions = false;
      }
    });

    this.selectedFeatures = this.form.value.optionalFeatures?.map(feature => {
      const metadata = this.systemFormMetadata.optionalFeatures.find(f => f.name === feature.name);
      return metadata?.friendlyName;
    }).filter(name => name) || [];
  }

  /** Handler for when a site-select in one of the dynamic controls emits a selected site. */
  onSiteSelected(site: Organization | null): void {
    this.selectedSite = site;
  }

  /**
   * Event handler for optional feature multi-select menu.
   * @param event The DOM event.
   */
  onFeatureSelect(event: any) {
    const featureMetadata = this.systemFormMetadata.optionalFeatures.find(feature => feature.friendlyName === event.source.value);
    const selected = event.source.selected;

    // Toggle feature if the user raised the event
    if (event.isUserInput) {
      if (selected) {
        let feature = this.form.value.optionalFeatures.find(feature => feature.name === featureMetadata.name);
  
        if (!feature) {
          this.optionalFeatures.push(this.getFeatureFormGroup(featureMetadata));
        }
      }
      else {
        let featureIndex = this.optionalFeatures.value.findIndex(feature => feature.name === featureMetadata.name);
        this.optionalFeatures.removeAt(featureIndex);
      }
    }

    this.featureSelected.emit({
      featureMetadata: featureMetadata,
      selected: selected,
      isUserInput: event.isUserInput
    });
  }

  /** Handler to bubble up featureSelected events from recursive components. */
  nestedFeatureSelected(args: FeatureSelectedEventArgs): void {
    this.featureSelected.emit(args);
  }

  /** Gets the component's metadata by name. */
  getOptionalFeature(featureName): DeploymentTypeMetadata {
    return this.systemFormMetadata.optionalFeatures.find(feature => feature.name === featureName);
  }

  /** Return a new FormGroup built from the given metadata with default values. */
  getFeatureFormGroup(feature: DeploymentTypeMetadata): FormGroup {
    return this.systemFormService.getSystemForm(feature);
  }

  private isDeploymentScopedFeature(featureName: string): boolean {
    return (this.systemFormMetadata?.deploymentFeatures || []).some(feature => feature.name === featureName);
  }

  ngOnDestroy(): void {
    this.featureSelected.emit({
      featureMetadata: this.systemFormMetadata,
      selected: false,
      isUserInput: false
    });
  }
}
